import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import { toast } from 'react-toastify';
import api from '../axiosConfig';
import { DEFAULT_CHECKLIST_MAX_QUESTIONS, resolveChecklistMaxQuestions, toChecklistQuestionRows } from '../utils/checklistConfiguration';

const MAX_QUESTION_LENGTH = 500;
const ACCESS_REVOKED_MESSAGE = 'Inspection question editing is no longer available for your account in this register.';

export default function InspectionQuestionsDialog({ actionSx }) {
  const [canManage, setCanManage] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [maxQuestions, setMaxQuestions] = useState(DEFAULT_CHECKLIST_MAX_QUESTIONS);
  const [questions, setQuestions] = useState(() => toChecklistQuestionRows());
  const [error, setError] = useState('');
  const mounted = useRef(false);
  const openRef = useRef(false);
  const pendingSave = useRef(false);
  const requestVersion = useRef(0);

  useEffect(() => {
    mounted.current = true;
    const refreshAvailability = async () => {
      if (openRef.current || pendingSave.current) return;
      const version = ++requestVersion.current;
      try {
        const { data } = await api.get('/api/settings/checklist');
        if (mounted.current && version === requestVersion.current) {
          setCanManage(data?.canManageQuestions === true);
        }
      } catch {
        if (mounted.current && version === requestVersion.current) setCanManage(false);
      }
    };
    refreshAvailability();
    window.addEventListener('focus', refreshAvailability);
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
      window.removeEventListener('focus', refreshAvailability);
    };
  }, []);

  const close = () => {
    if (pendingSave.current) return;
    requestVersion.current += 1;
    openRef.current = false;
    setOpen(false);
    setLoading(false);
    setSettingsLoaded(false);
    setMaxQuestions(DEFAULT_CHECKLIST_MAX_QUESTIONS);
    setQuestions(toChecklistQuestionRows());
    setError('');
  };

  const openEditor = async () => {
    if (openRef.current || pendingSave.current) return;
    openRef.current = true;
    setOpen(true);
    setLoading(true);
    setSettingsLoaded(false);
    setMaxQuestions(DEFAULT_CHECKLIST_MAX_QUESTIONS);
    setQuestions(toChecklistQuestionRows());
    setError('');
    const version = ++requestVersion.current;
    try {
      const { data } = await api.get('/api/settings/checklist');
      if (!mounted.current || version !== requestVersion.current) return;
      const permitted = data?.canManageQuestions === true;
      setCanManage(permitted);
      if (permitted) {
        const approvedLimit = resolveChecklistMaxQuestions(data.maxQuestions);
        setMaxQuestions(approvedLimit);
        setQuestions(toChecklistQuestionRows(data.questions, approvedLimit));
        setSettingsLoaded(true);
      }
      else setError(ACCESS_REVOKED_MESSAGE);
    } catch (failure) {
      if (!mounted.current || version !== requestVersion.current) return;
      if ([401, 403].includes(failure?.response?.status)) {
        setCanManage(false);
        setError(ACCESS_REVOKED_MESSAGE);
      } else {
        setError('Could not load inspection questions. Close this dialog and try again.');
      }
    } finally {
      if (mounted.current && version === requestVersion.current) setLoading(false);
    }
  };

  const save = async () => {
    if (pendingSave.current || loading || !settingsLoaded || !canManage) return;
    const configuredQuestions = questions.map((question) => question.trim()).filter(Boolean);
    if (configuredQuestions.length > maxQuestions) {
      setError(`This register allows up to ${maxQuestions} inspection questions.`);
      return;
    }
    if (!configuredQuestions.length) {
      setError('Enter at least one inspection question.');
      return;
    }
    if (configuredQuestions.some((question) => question.length > MAX_QUESTION_LENGTH)) {
      setError(`Each question must be ${MAX_QUESTION_LENGTH} characters or fewer.`);
      return;
    }
    pendingSave.current = true;
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put('/api/settings/checklist/questions', { questions: configuredQuestions });
      if (!mounted.current) return;
      setCanManage(data?.canManageQuestions === true);
      pendingSave.current = false;
      close();
      toast.success('Inspection questions saved.');
    } catch (failure) {
      if (!mounted.current) return;
      if ([401, 403].includes(failure?.response?.status)) {
        setCanManage(false);
        setError(ACCESS_REVOKED_MESSAGE);
      } else {
        setError(failure?.response?.data?.message || 'Could not save inspection questions. Please try again.');
      }
    } finally {
      pendingSave.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <>
      {canManage && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ChecklistOutlinedIcon fontSize="small" />}
          onClick={openEditor}
          sx={actionSx}
        >
          Inspections
        </Button>
      )}
      <Dialog
        open={open}
        onClose={close}
        disableEscapeKeyDown={saving}
        fullWidth
        maxWidth="sm"
        aria-labelledby="inspection-questions-title"
        aria-describedby="inspection-questions-description"
      >
        <DialogTitle id="inspection-questions-title" sx={{ fontWeight: 700 }}>Inspection questions</DialogTitle>
        <DialogContent dividers>
          <Typography id="inspection-questions-description" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure up to {maxQuestions} questions for this register, with a maximum of 500 characters each.
            Blank fields are omitted. Existing inspection records keep their original questions.
          </Typography>
          {loading ? (
            <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} aria-hidden="true" />
              <Typography variant="body2">Loading inspection questions…</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {canManage && settingsLoaded && questions.map((question, index) => (
                <TextField
                  key={index}
                  id={`inspection-question-${index + 1}`}
                  label={`Question ${index + 1}`}
                  value={question}
                  onChange={(event) => {
                    const value = event.target.value;
                    setQuestions((previous) => previous.map((item, position) => position === index ? value : item));
                    setError('');
                  }}
                  disabled={saving}
                  multiline
                  minRows={2}
                  maxRows={5}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: MAX_QUESTION_LENGTH }}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        {error && <Alert severity="error" sx={{ mx: 3, mt: 2 }}>{error}</Alert>}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={close} disabled={saving}>{canManage ? 'Cancel' : 'Close'}</Button>
          {canManage && (
            <Button variant="contained" onClick={save} disabled={loading || saving || !settingsLoaded}>
              {saving ? 'Saving…' : 'Save questions'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
